/**
 * VictoryScreen — Fullscreen credits overlay after defeating the Ender Dragon.
 *
 * Features:
 * - Auto-scrolling credits (Star Wars style) over ~20 seconds
 * - Stats display: time played, blocks mined, mobs killed
 * - Credits for all AI tools used in the project
 * - "Volver al Mundo" button appears after 5 seconds
 * - Clean monospace aesthetic on dark purple background
 */
export class VictoryScreen {
  constructor() {
    this._overlay = null;
    this._content = null;
    this._button = null;
    this._open = false;
    this._buttonTimeout = null;
    this.onComplete = null;

    this._buildUI();
  }

  get isOpen() {
    return this._open;
  }

  _buildUI() {
    // --- Fullscreen overlay ---
    const overlay = document.createElement('div');
    overlay.style.cssText = [
      'position:fixed',
      'top:0',
      'left:0',
      'width:100%',
      'height:100%',
      'background:#0A0012',
      'z-index:960',
      'opacity:0',
      'pointer-events:none',
      'transition:opacity 1.5s ease',
      'display:flex',
      'flex-direction:column',
      'align-items:center',
      'justify-content:flex-end',
      'overflow:hidden',
      'font-family:Courier New, monospace',
    ].join(';');
    this._overlay = overlay;

    // --- Scrolling content wrapper ---
    const content = document.createElement('div');
    content.style.cssText = [
      'position:absolute',
      'bottom:0',
      'left:50%',
      'transform:translateX(-50%)',
      'width:600px',
      'max-width:90%',
      'text-align:center',
      'color:#fff',
      'padding:40px 20px 120px',
    ].join(';');
    this._content = content;
    overlay.appendChild(content);

    // --- "Volver al Mundo" button ---
    const btn = document.createElement('button');
    btn.textContent = 'Volver al Mundo';
    btn.style.cssText = [
      'position:fixed',
      'bottom:40px',
      'left:50%',
      'transform:translateX(-50%)',
      'font-family:Courier New, monospace',
      'font-size:18px',
      'color:#0A0012',
      'background:#D4A017',
      'border:none',
      'padding:12px 32px',
      'cursor:pointer',
      'z-index:961',
      'opacity:0',
      'pointer-events:none',
      'transition:opacity 0.8s ease, background 0.2s ease',
      'letter-spacing:1px',
    ].join(';');
    btn.addEventListener('mouseenter', () => { btn.style.background = '#E8B828'; });
    btn.addEventListener('mouseleave', () => { btn.style.background = '#D4A017'; });
    btn.addEventListener('click', () => {
      this.hide();
      if (this.onComplete) this.onComplete();
    });
    this._button = btn;
    overlay.appendChild(btn);

    document.body.appendChild(overlay);
  }

  /**
   * Build the credits HTML from player stats.
   * @param {{ timePlayed: number, blocksMined: number, mobsKilled: number }} stats
   * @returns {string}
   */
  _buildCredits(stats) {
    const minutes = Math.floor((stats.timePlayed || 0) / 60);
    const seconds = Math.floor((stats.timePlayed || 0) % 60);
    const timeStr = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

    return `
      <div style="margin-bottom:60px;">
        <div style="font-size:28px;color:#D4A017;text-shadow:0 0 20px rgba(212,160,23,0.4);letter-spacing:3px;">
          &iexcl;Has derrotado al Drag&oacute;n del End!
        </div>
      </div>

      <div style="margin-bottom:50px;">
        <div style="font-size:18px;color:#9B7ECF;letter-spacing:2px;">FedeWorld v3.0</div>
      </div>

      <div style="margin-bottom:50px;line-height:2.2;">
        <div style="font-size:14px;color:#8B7BA8;margin-bottom:10px;letter-spacing:1px;">
          &mdash; ESTAD&Iacute;STICAS &mdash;
        </div>
        <div style="font-size:16px;">Tiempo jugado: <span style="color:#D4A017;">${timeStr}</span></div>
        <div style="font-size:16px;">Bloques minados: <span style="color:#D4A017;">${stats.blocksMined || 0}</span></div>
        <div style="font-size:16px;">Mobs eliminados: <span style="color:#D4A017;">${stats.mobsKilled || 0}</span></div>
      </div>

      <div style="margin-bottom:50px;line-height:2.4;">
        <div style="font-size:14px;color:#8B7BA8;margin-bottom:10px;letter-spacing:1px;">
          &mdash; CR&Eacute;DITOS &mdash;
        </div>
        <div style="font-size:16px;">Creado por <span style="color:#D4A017;">Fede</span></div>
        <div style="font-size:14px;color:#ccc;">Desarrollado con Claude (Anthropic)</div>
        <div style="font-size:14px;color:#ccc;">Videos: Runway</div>
        <div style="font-size:14px;color:#ccc;">Voz: ElevenLabs</div>
        <div style="font-size:14px;color:#ccc;">M&uacute;sica: Suno</div>
      </div>

      <div style="margin-bottom:60px;line-height:1.8;">
        <div style="font-size:15px;color:#9B7ECF;font-style:italic;">
          Todo gran mundo comienza en silencio.<br>
          Con una idea y el poder de crear.<br>
          Tu mundo seguir&aacute; creciendo.
        </div>
      </div>

      <div style="margin-bottom:80px;">
        <div style="font-size:13px;color:#6B5B8A;letter-spacing:2px;">Gracias por jugar</div>
      </div>
    `;
  }

  /**
   * Show the victory screen with scrolling credits.
   * @param {{ timePlayed: number, blocksMined: number, mobsKilled: number }} stats
   */
  show(stats = {}) {
    if (this._open) return;
    this._open = true;

    this._content.innerHTML = this._buildCredits(stats);

    // Reset scroll position: content starts below the viewport
    const contentHeight = this._content.scrollHeight + 200;
    this._content.style.transition = 'none';
    this._content.style.transform = `translateX(-50%) translateY(${window.innerHeight}px)`;

    // Fade in overlay
    this._overlay.style.pointerEvents = 'auto';
    this._overlay.style.opacity = '1';

    // Start scroll animation after a short delay
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scrollDist = window.innerHeight + contentHeight;
        const duration = Math.max(20, scrollDist / 40);
        this._content.style.transition = `transform ${duration}s linear`;
        this._content.style.transform = `translateX(-50%) translateY(-${contentHeight}px)`;
      });
    });

    // Show button after 5 seconds
    this._buttonTimeout = setTimeout(() => {
      this._button.style.opacity = '1';
      this._button.style.pointerEvents = 'auto';
    }, 5000);
  }

  /** Hide the victory screen and clean up. */
  hide() {
    if (!this._open) return;
    this._open = false;

    if (this._buttonTimeout) {
      clearTimeout(this._buttonTimeout);
      this._buttonTimeout = null;
    }

    this._overlay.style.opacity = '0';
    this._overlay.style.pointerEvents = 'none';
    this._button.style.opacity = '0';
    this._button.style.pointerEvents = 'none';

    // Reset content after fade
    setTimeout(() => {
      this._content.style.transition = 'none';
      this._content.style.transform = 'translateX(-50%) translateY(0)';
      this._content.innerHTML = '';
    }, 1600);
  }
}
